import { idMessages, enMessages } from "./messages/index.ts";
import { DEFAULT_LOCALE, type Locale } from "./config.ts";

type Nested = { [k: string]: string | Nested };
export type FlatMessages = Record<string, string>;

/** Nested namespace objects -> { "ns.path.to.key": "text" }. */
export function flatten(obj: Nested, prefix = "", out: FlatMessages = {}): FlatMessages {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[key] = v;
    else flatten(v, key, out);
  }
  return out;
}

const cache: Partial<Record<Locale, FlatMessages>> = {};

export function getMessages(locale: Locale): FlatMessages {
  return (cache[locale] ??= flatten((locale === "id" ? idMessages : enMessages) as unknown as Nested));
}

// --- typed keys -------------------------------------------------------------------------------
type Paths<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Paths<T[K], `${P}${K}.`>;
}[keyof T & string];
type StripPlural<K extends string> = K extends `${infer B}_${"zero" | "one" | "other"}` ? B : K;

/** Every key that exists in the Bahasa dictionary (the source of truth). Plural variants collapse to their base key. */
export type MessageKey = StripPlural<Paths<typeof idMessages>>;
export type Params = Record<string, string | number | null | undefined>;

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = params[name];
    return v === undefined || v === null ? m : String(v);
  });
}

/**
 * Looks up a key. If `params.count` is a number, `key_zero` / `key_one` / `key_other` variants are
 * chosen with Intl.PluralRules. Falls back to the default locale, then to the key itself (visible in
 * the UI and caught by the key-parity tests).
 */
export function translate(locale: Locale, key: string, params?: Params): string {
  const lookup = (loc: Locale): string | undefined => {
    const msgs = getMessages(loc);
    if (params && typeof params.count === "number") {
      if (params.count === 0 && msgs[`${key}_zero`] !== undefined) return msgs[`${key}_zero`];
      const cat = new Intl.PluralRules(loc === "id" ? "id-ID" : "en-US").select(params.count);
      const plural = msgs[`${key}_${cat}`] ?? msgs[`${key}_other`];
      if (plural !== undefined) return plural;
    }
    return msgs[key];
  };
  const found = lookup(locale) ?? (locale !== DEFAULT_LOCALE ? lookup(DEFAULT_LOCALE) : undefined);
  return found === undefined ? key : interpolate(found, params);
}
